import { tv } from 'tailwind-variants';
import { sendRespawn } from '../../network/NetworkClient';
import { clearColonyLost, isColonyLost } from '../../state/colonyLost';
import {
  closeControlsOverlay,
  controlsPhase,
  openControlsOverlay,
} from '../../state/controlsOverlay';
import {
  cancelRespawnConfirmation,
  canRespawn,
  closeEscMenu,
  isEscMenuOpen,
  isRespawnConfirming,
  startRespawnConfirmation,
} from '../../state/escMenu';
import { t } from '../../state/i18n';
import { goFullscreen, isFullscreen } from '../../state/splash';

const overlay = tv({
  base: 'fixed inset-0 flex items-center justify-center z-[9997]',
});

const backdrop = tv({
  base: 'absolute inset-0 bg-slate-950/85 backdrop-blur-sm',
});

const card = tv({
  base: 'relative w-full max-w-sm mx-4 bg-gradient-to-b from-slate-900 to-slate-950 rounded-2xl border border-slate-700/40 shadow-2xl overflow-hidden',
});

const headerStyle = tv({
  base: 'px-6 py-5 border-b border-slate-800/50 text-center',
});

const contentStyle = tv({
  base: 'p-6 space-y-3',
});

const resumeButton = tv({
  base: 'w-full px-6 py-3 rounded-lg font-mono text-sm font-medium transition-all duration-200 bg-slate-700 hover:bg-slate-600 text-white cursor-pointer',
});

const respawnButton = tv({
  base: 'w-full px-6 py-3 rounded-lg font-mono text-sm font-medium transition-all duration-200',
  variants: {
    disabled: {
      true: 'bg-slate-800 text-slate-500 cursor-not-allowed',
      false: 'bg-red-600 hover:bg-red-500 text-white cursor-pointer',
    },
  },
});

const confirmCard = tv({
  base: 'relative w-full max-w-sm mx-4 bg-gradient-to-b from-slate-900 to-slate-950 rounded-2xl border border-red-500/20 shadow-2xl shadow-red-500/10 overflow-hidden',
});

const confirmButton = tv({
  base: 'flex-1 px-4 py-3 rounded-lg font-mono text-sm font-medium transition-all duration-200 bg-red-600 hover:bg-red-500 text-white cursor-pointer',
});

const cancelButton = tv({
  base: 'flex-1 px-4 py-3 rounded-lg font-mono text-sm font-medium transition-all duration-200 bg-slate-700 hover:bg-slate-600 text-white cursor-pointer',
});

const MainMenu = () => {
  const respawnEnabled = canRespawn.value;

  const handleRespawnClick = () => {
    if (respawnEnabled) {
      startRespawnConfirmation();
    }
  };

  const controlsVisible = controlsPhase.value === 'visible';

  const handleControlsClick = () => {
    if (controlsVisible) {
      closeControlsOverlay();
    } else {
      openControlsOverlay();
    }
    closeEscMenu();
  };

  const handleFullscreenClick = () => {
    goFullscreen();
    closeEscMenu();
  };

  return (
    <div class={card()}>
      <div class={headerStyle()}>
        <h2 class="text-lg font-semibold text-white">{t('ui.escMenu.title')}</h2>
      </div>
      <div class={contentStyle()}>
        <button type="button" class={resumeButton()} onClick={closeEscMenu}>
          {t('ui.escMenu.resume')}
        </button>
        <button type="button" class={resumeButton()} onClick={handleControlsClick}>
          {controlsVisible ? t('ui.escMenu.hideControls') : t('ui.escMenu.showControls')}
        </button>
        {!isFullscreen.value && (
          <button type="button" class={resumeButton()} onClick={handleFullscreenClick}>
            {t('ui.escMenu.goFullscreen')}
          </button>
        )}
        <div class="relative group">
          <button
            type="button"
            class={respawnButton({ disabled: !respawnEnabled })}
            onClick={handleRespawnClick}
            disabled={!respawnEnabled}
          >
            {t('ui.escMenu.respawn')}
          </button>
          {!respawnEnabled && (
            <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-xs text-slate-400 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              {t('ui.escMenu.respawnDisabled')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ConfirmDialog = () => {
  const handleConfirm = () => {
    if (isColonyLost.value) {
      clearColonyLost();
    }
    sendRespawn();
    closeEscMenu();
  };

  return (
    <div class={confirmCard()}>
      <div class={headerStyle()}>
        <h2 class="text-lg font-semibold text-white">{t('ui.escMenu.confirmTitle')}</h2>
      </div>
      <div class={contentStyle()}>
        <div class="bg-slate-800/50 border border-slate-700/30 rounded-lg p-4">
          <p class="text-sm text-slate-300 leading-relaxed">{t('ui.escMenu.confirmMessage')}</p>
        </div>
        <div class="flex gap-3">
          <button type="button" class={cancelButton()} onClick={cancelRespawnConfirmation}>
            {t('ui.escMenu.cancelButton')}
          </button>
          <button type="button" class={confirmButton()} onClick={handleConfirm}>
            {t('ui.escMenu.confirmButton')}
          </button>
        </div>
      </div>
    </div>
  );
};

export const EscMenu = () => {
  if (!isEscMenuOpen.value) {
    return null;
  }

  return (
    <div class={overlay()}>
      <div class={backdrop()} />
      {isRespawnConfirming.value ? <ConfirmDialog /> : <MainMenu />}
    </div>
  );
};
